"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getPriceRestrictions = exports.parseTimeRestriction = exports.parseDateRestriction = exports.parseKwhRestriction = exports.parseDayOfWeekRestriction = exports.parseDuration = exports.parseDurationRestriction = void 0;
const tslib_1 = require("tslib");
const moment_1 = (0, tslib_1.__importDefault)(require("moment"));
const models_1 = require("../models");
const localization_1 = (0, tslib_1.__importStar)(require("../localization"));
const parseDurationRestriction = ({ restrictions, compact = false, language, }) => {
    (0, localization_1.adjustLanguage)(language);
    const { min_duration, max_duration } = restrictions;
    // If min duration is lower than 30 seconds, don't display it
    const minDuration = min_duration && min_duration >= 30
        ? (0, exports.parseDuration)({ durationInSeconds: min_duration, compact, language })
        : undefined;
    const maxDuration = max_duration
        ? (0, exports.parseDuration)({
            durationInSeconds: max_duration,
            omitNumber: !min_duration,
            compact,
            language,
        })
        : undefined;
    if (minDuration && maxDuration) {
        return compact
            ? `${minDuration} - ${maxDuration}`
            : localization_1.default.t('ocpiRestrictionsBetweenDuration', { minDuration, maxDuration });
    }
    if (maxDuration) {
        return compact
            ? `< ${maxDuration}`
            : localization_1.default.t('ocpiRestrictionsFirstDuration', { maxDuration });
    }
    if (minDuration) {
        return compact
            ? `> ${minDuration}`
            : localization_1.default.t('ocpiRestrictionsAfterDuration', { minDuration });
    }
    return '';
};
exports.parseDurationRestriction = parseDurationRestriction;
const parseDuration = ({ durationInSeconds, compact = false, omitNumber = false, language, }) => {
    if (!durationInSeconds || isNaN(durationInSeconds)) {
        return '';
    }
    (0, localization_1.adjustLanguage)(language);
    const seconds = Math.round(durationInSeconds / 60) * 60;
    const duration = moment_1.default.duration({ seconds });
    const days = Math.floor(duration.asDays());
    const hours = duration.hours();
    const minutes = duration.minutes();
    if (compact) {
        return `${days ? `${days} ${localization_1.default.t('day', { count: days })} ` : ''}${hours ? `${hours} h` : ''} ${minutes ? `${minutes} min` : ''}`.trim();
    }
    const dayString = days
        ? `${days === 1 && omitNumber ? '' : `${days} `}${localization_1.default.t(omitNumber ? 'theDay' : 'day', {
            count: days,
        })}`
        : '';
    const hourString = hours
        ? `${hours === 1 && omitNumber ? '' : `${hours} `}${localization_1.default.t(omitNumber ? 'theHour' : 'hour', {
            count: hours,
        })}`
        : '';
    const minuteString = minutes
        ? `${minutes === 1 && omitNumber && !hours ? '' : `${minutes} `}${localization_1.default.t(omitNumber ? 'theMinute' : 'minute', { count: minutes })}`
        : '';
    const separator1 = days && hours ? ` ${localization_1.default.t('and')} ` : '';
    const separator2 = hours && minutes ? ` ${localization_1.default.t('and')} ` : '';
    return `${dayString}${separator1}${hourString}${separator2}${minuteString}`;
};
exports.parseDuration = parseDuration;
const weekdayNumbers = {
    [models_1.DayOfWeek.MONDAY]: 1,
    [models_1.DayOfWeek.TUESDAY]: 2,
    [models_1.DayOfWeek.WEDNESDAY]: 3,
    [models_1.DayOfWeek.THURSDAY]: 4,
    [models_1.DayOfWeek.FRIDAY]: 5,
    [models_1.DayOfWeek.SATURDAY]: 6,
    [models_1.DayOfWeek.SUNDAY]: 7,
};
const parseDayOfWeekRestriction = ({ weekdays, compact = false, language, }) => {
    (0, localization_1.adjustLanguage)(language);
    let consecutiveDays = [];
    let description = '';
    const addConsecutiveDaysToDescription = () => {
        description += `${description !== '' ? ', ' : ''}${compact
            ? localization_1.default.t(consecutiveDays[0]).substring(0, 3)
            : localization_1.default.t(consecutiveDays[0])}${consecutiveDays.length > 1
            ? compact
                ? `-${localization_1.default
                    .t(consecutiveDays[consecutiveDays.length - 1])
                    .substring(0, 3)}`
                : ` - ${localization_1.default.t(consecutiveDays[consecutiveDays.length - 1])}`
            : ''}`;
    };
    weekdays
        .slice()
        .sort((a, b) => weekdayNumbers[a] - weekdayNumbers[b])
        .forEach((day, i) => {
        if (consecutiveDays.length > 0 &&
            weekdayNumbers[day] !==
                weekdayNumbers[consecutiveDays[consecutiveDays.length - 1]] + 1) {
            addConsecutiveDaysToDescription();
            consecutiveDays = [];
        }
        consecutiveDays.push(day);
    });
    if (consecutiveDays.length > 0) {
        addConsecutiveDaysToDescription();
    }
    return description;
};
exports.parseDayOfWeekRestriction = parseDayOfWeekRestriction;
const parseKwhRestriction = ({ restrictions: { min_kwh, max_kwh }, compact = false, language, }) => {
    (0, localization_1.adjustLanguage)(language);
    const minKwh = min_kwh && `${min_kwh} kWh`;
    const maxKwh = max_kwh && `${max_kwh} kWh`;
    if (minKwh && maxKwh) {
        return compact
            ? `${minKwh} - ${maxKwh}`
            : localization_1.default.t('ocpiRestrictionsKwhBetween', { minKwh, maxKwh });
    }
    if (minKwh) {
        return compact
            ? `> ${minKwh}`
            : localization_1.default.t('ocpiRestrictionsKwhAbove', { minKwh });
    }
    if (maxKwh) {
        return compact
            ? `< ${maxKwh}`
            : localization_1.default.t('ocpiRestrictionsKwhLessThan', { maxKwh });
    }
    return '';
};
exports.parseKwhRestriction = parseKwhRestriction;
/**
 * Parses the start and end dates into a localized format based on the given params.
 *
 * @param {Object} restrictions - The object that contains the start and end date restrictions to be parsed.
 * @param {MomentFormatSpecification} dateFormat - The date format to use for parsing. Defaults to 'YYYY-MM-DD'.
 * @param {boolean} compact - A flag indicating whether the parsed date range should be in a compact format.Defaults to true.
 * @param {SupportedLanguages} language - The language to use for localization.
 *
 * @returns {string} A string representing the parsed date restriction, or an empty string if either the start or end date is missing.
 */
const parseDateRestriction = ({ restrictions: { start_date, end_date }, dateFormat = 'YYYY-MM-DD', compact = true, language, }) => {
    (0, localization_1.adjustLanguage)(language);
    if (!start_date || !end_date) {
        return '';
    }
    const startDate = (0, moment_1.default)(start_date, dateFormat).startOf('day');
    const endDate = (0, moment_1.default)(end_date, dateFormat).endOf('day');
    if (!compact) {
        return `${startDate.format('L HH:mm')} - ${endDate.format('L HH:mm')}`;
    }
    return `${startDate.format('L')} - ${endDate.format('L')}`;
};
exports.parseDateRestriction = parseDateRestriction;
const parseTimeRestriction = ({ restrictions: { start_time, end_time }, compact = false, language, }) => {
    (0, localization_1.adjustLanguage)(language);
    if (!start_time || !end_time) {
        return '';
    }
    if (compact &&
        start_time.indexOf(':00') !== -1 &&
        end_time.indexOf(':00') !== -1) {
        return `${start_time.replace(':00', '')} - ${end_time.replace(':00', '')}`;
    }
    return `${start_time} - ${end_time}`;
};
exports.parseTimeRestriction = parseTimeRestriction;
const getPriceRestrictions = ({ restrictions, compact = false, language, excludedRestrictions = [], }) => {
    (0, localization_1.adjustLanguage)(language);
    const restrictionArray = [];
    const checkIfRestrictionIsExcluded = (restrictionKeys) => restrictionKeys.some((k) => excludedRestrictions.includes(k));
    if ((restrictions === null || restrictions === void 0 ? void 0 : restrictions.day_of_week) &&
        !checkIfRestrictionIsExcluded(['day_of_week'])) {
        restrictionArray.push((0, exports.parseDayOfWeekRestriction)({
            weekdays: restrictions.day_of_week,
            compact,
            language,
        }));
    }
    excludedRestrictions;
    if (((restrictions === null || restrictions === void 0 ? void 0 : restrictions.start_date) || (restrictions === null || restrictions === void 0 ? void 0 : restrictions.end_date)) &&
        !checkIfRestrictionIsExcluded(['start_date', 'end_date'])) {
        restrictionArray.push((0, exports.parseDateRestriction)({ restrictions, compact, language }));
    }
    if ((restrictions === null || restrictions === void 0 ? void 0 : restrictions.start_time) &&
        restrictions.end_time &&
        !checkIfRestrictionIsExcluded(['start_time', 'end_time'])) {
        restrictionArray.push((0, exports.parseTimeRestriction)({ restrictions, compact, language }));
    }
    if (((restrictions === null || restrictions === void 0 ? void 0 : restrictions.min_kwh) || (restrictions === null || restrictions === void 0 ? void 0 : restrictions.max_kwh)) &&
        !checkIfRestrictionIsExcluded(['min_kwh', 'max_kwh'])) {
        restrictionArray.push((0, exports.parseKwhRestriction)({ restrictions, compact, language }));
    }
    if ((restrictions === null || restrictions === void 0 ? void 0 : restrictions.min_duration) ||
        ((restrictions === null || restrictions === void 0 ? void 0 : restrictions.max_duration) &&
            !checkIfRestrictionIsExcluded(['min_duration', 'max_duration']))) {
        const durationRestriction = (0, exports.parseDurationRestriction)({
            restrictions,
            compact,
            language,
        });
        if (durationRestriction !== '') {
            restrictionArray.push((0, exports.parseDurationRestriction)({ restrictions, compact, language }));
        }
    }
    return restrictionArray;
};
exports.getPriceRestrictions = getPriceRestrictions;
